import { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MedicationsDispensedContext } from "../MedicationsDispensedContext";
import DailyTotalBarChart from "./DailyTotalBarChart/DailyTotalBarChart";
import { ReactComponent as ArrowUpCircle } from "../../../../../../assets/arrow-up-circle.svg";
import { ReactComponent as ArrowDownCircle } from "../../../../../../assets/arrow-down-circle.svg";
// import { getLocalizedDate } from "../../../../../../functions/localization";
import {
  getNumberOfWeekDayInDateRange,
  getNumberOfDaysInDateRange,
} from "../../../../../../functions/dates";
import styles from "./MetricsChartView.module.css";
const MetricsChartViewCanisters = ({
  user,
  selectedDayCanister,
  onSelectedDayCanister,
  selectedDateRange,
  applicableDates,
  onApplicableDates,
  canisterProducts,
  onCanisterProducts,
  onCanisterProductsSelectedDay,
  canisterProductSelectionLookup,
  onCanisterProductSelectionLookup,
  onShowDropdowns,
}) => {
  const { t } = useTranslation("trupakDashboard");

  const { batchesCurrentWeek, batchesLastWeek, batchesSelectedDateRange } =
    useContext(MedicationsDispensedContext);

  const [
    totalDispensedCanisterSelectedDateRange,
    setTotalDispensedCanisterSelectedDateRange,
  ] = useState({
    U: { qty: 0, packageIds: [] },
    M: { qty: 0, packageIds: [] },
    T: { qty: 0, packageIds: [] },
    W: { qty: 0, packageIds: [] },
    R: { qty: 0, packageIds: [] },
    F: { qty: 0, packageIds: [] },
    S: { qty: 0, packageIds: [] },
    total: 0,
  });

  const [canisterBargraphData, setCanisterBargraphData] = useState([]);
  const [classNameForBargraph, setClassNameforBargraph] = useState(null);

  const [
    totalPillsDispensedFromCanisterLastWeek,
    setTotalPillsDispensedFromCanisterLastWeek,
  ] = useState(0);

  const [
    totalPillsDispensedFromCanisterCurrentWeek,
    setTotalPillsDispensedFromCanisterCurrentWeek,
  ] = useState(0);

  const [
    dailyAverageCanisterSelectedDateRange,
    setDailyAverageCanisterSelectedDateRange,
  ] = useState(0); // Used for Daily Average calculation

  const combineTwoArrays = (array1, array2) => [
    ...new Set([...array1, ...array2]),
  ];

  const tableTitle = (
    <div className={styles.MetricChartView__bargraphTitle}>
      <h2 className={styles.MetricChartView__bargraphTitleMain}>
        {t("Daily Canister Usage")}
      </h2>
    </div>
  );

  useEffect(() => {
    /**
     * Very important
     *  - Needed to clear canisterProducts and
     *      dailyAverageCanisterSelectedDateRange if there are no trays
     **/
    const canisterBargraphDataWithValues = canisterBargraphData.filter(
      (dataSet) => dataSet.y > 0,
    );

    if (!canisterBargraphDataWithValues.length) {
      onCanisterProducts([]);
      setDailyAverageCanisterSelectedDateRange(0);
    }
  }, [canisterBargraphData, onCanisterProducts]);

  // SELECTED DATE RANGE
  useEffect(() => {
    /**
     * If batchesSelectedDateRange array has length, sets:
     *  - readyForServerRequest
     *  - totalDispensedCanisterSelectedDateRange
     *  - canisterBargraphData
     *  - canisteryProductSelectionLookup,
     *  - dispensingOrderIds
     *  - packagedIdsList
     *
     * Else, resets the following to default (starting) values:
     *  - canisterProducts,
     *  - canisterProductsSelectedDay,
     *  - canisterProductSelectionLookup,
     *  - canisterBargraphData,
     *  - selectedCanisterDate
     *  - readyForServerRequest
     **/
    if (batchesSelectedDateRange.length) {
      let canisterPackageIdsDate = [];
      let canisterProducts_temp = [];
      let tempCanisterIdsSunday = [];
      let tempCanisterIdsMonday = [];
      let tempCanisterIdsTuesday = [];
      let tempCanisterIdsWednesday = [];
      let tempCanisterIdsThursday = [];
      let tempCanisterIdsFriday = [];
      let tempCanisterIdsSaturday = [];
      let totalNumberCanisters = 0;
      let tempNumberCanistersSunday = 0;
      let tempNumberCanistersMonday = 0;
      let tempNumberCanistersTuesday = 0;
      let tempNumberCanistersWednesday = 0;
      let tempNumberCanistersThursday = 0;
      let tempNumberCanistersFriday = 0;
      let tempNumberCanistersSaturday = 0;
      let dayOfWeek;
      let tempCanisterProductSelectionLookup = {};

      const incrementQuantityForDayOfWeek = (day, canisterIds) => {
        switch (day) {
          case 0:
            tempCanisterIdsSunday = combineTwoArrays(
              tempCanisterIdsSunday,
              canisterIds,
            );
            tempNumberCanistersSunday = tempCanisterIdsSunday.length;
            break;
          case 1:
            tempCanisterIdsMonday = combineTwoArrays(
              tempCanisterIdsMonday,
              canisterIds,
            );
            break;
          case 2:
            tempCanisterIdsTuesday = combineTwoArrays(
              tempCanisterIdsTuesday,
              canisterIds,
            );
            tempNumberCanistersTuesday = tempCanisterIdsTuesday.length;
            break;
          case 3:
            tempCanisterIdsWednesday = combineTwoArrays(
              tempCanisterIdsWednesday,
              canisterIds,
            );
            tempNumberCanistersWednesday = tempCanisterIdsWednesday.length;
            break;
          case 4:
            tempCanisterIdsThursday = combineTwoArrays(
              tempCanisterIdsThursday,
              canisterIds,
            );
            tempNumberCanistersThursday = tempCanisterIdsThursday.length;
            break;
          case 5:
            tempCanisterIdsFriday = combineTwoArrays(
              tempCanisterIdsFriday,
              canisterIds,
            );
            tempNumberCanistersFriday = tempCanisterIdsFriday.length;
            break;
          case 6:
            tempCanisterIdsSaturday = combineTwoArrays(
              tempCanisterIdsSaturday,
              canisterIds,
            );
            break;
          default:
        }
      };

      for (const batchSelectedDateRange of batchesSelectedDateRange) {
        dayOfWeek = new Date(batchSelectedDateRange.createdAt).getDay(); // gets day of corresponding local time

        for (const dispensingDetail of batchSelectedDateRange.dispensingDetails) {
          if (!dispensingDetail.isTrayMed) {
            if (
              !canisterPackageIdsDate.includes(
                `${dispensingDetail.packageId};${dayOfWeek}`,
              )
            ) {
              canisterPackageIdsDate.push(
                `${dispensingDetail.packageId};${dayOfWeek}`,
              );
              canisterProducts_temp.push({
                dispensingOrderId: batchSelectedDateRange.dispensingOrderId,
                packageId: dispensingDetail.packageId,
                productName: dispensingDetail.productDescription,
                qty: !dispensingDetail.isPartial
                  ? dispensingDetail.quantity
                  : 0, // whole-pill quantity
                partialQty: dispensingDetail.isPartial
                  ? dispensingDetail.quantity
                  : 0, // partial-pill quantity
                dayOfWeek: dayOfWeek,
                manufacturer: dispensingDetail.productManufacturer,
                canisterIds: dispensingDetail?.canisterIds || ["abc", "def"],
                numberCanisters: (
                  dispensingDetail?.canisterIds || ["abc", "def"]
                ).length,
              });
            } else {
              if (
                canisterProducts_temp.some(
                  (p) =>
                    p.packageId === dispensingDetail.packageId &&
                    p.dayOfWeek === dayOfWeek,
                )
              ) {
                const product = canisterProducts_temp.find(
                  (p) =>
                    p.packageId === dispensingDetail.packageId &&
                    p.dayOfWeek === dayOfWeek,
                );
                if (product.dayOfWeek === dayOfWeek) {
                  if (dispensingDetail.isPartial) {
                    product.partialQty += dispensingDetail.quantity;
                  } else {
                    product.qty += dispensingDetail.quantity;
                  }
                  product.canisterIds = combineTwoArrays(product.canisterIds, [
                    "ghi",
                  ]);
                  product.numberCanisters = product.canisterIds.length;
                }
              }
            }
          }
        } // end for batch of
      }

      canisterProducts_temp.sort(compareByQuantityDescending);

      canisterProducts_temp.forEach((product) => {
        /*****************************************************************************************/
        totalNumberCanisters += product.numberCanisters;
        /*****************************************************************************************/
        incrementQuantityForDayOfWeek(product.dayOfWeek, product.canisterIds);
        tempCanisterProductSelectionLookup[product.packageId] = {
          selected: false,
          product: product.productName,
          manufacturer: product.manufacturer,
        };
      });

      setTotalDispensedCanisterSelectedDateRange({
        U: {
          qty: tempNumberCanistersSunday,
        },
        M: {
          qty: tempNumberCanistersMonday,
        },
        T: {
          qty: tempNumberCanistersTuesday,
        },
        W: {
          qty: tempNumberCanistersWednesday,
        },
        R: {
          qty: tempNumberCanistersThursday,
        },
        F: {
          qty: tempNumberCanistersFriday,
        },
        S: {
          qty: tempNumberCanistersSaturday,
        },
      });

      setCanisterBargraphData([
        {
          x: t("Sunday"),
          y: tempNumberCanistersSunday > 0 ? tempNumberCanistersSunday : 0,
        },
        {
          x: t("Monday"),
          y: tempNumberCanistersMonday > 0 ? tempNumberCanistersMonday : 0,
        },
        {
          x: t("Tuesday"),
          y: tempNumberCanistersTuesday > 0 ? tempNumberCanistersTuesday : 0,
        },
        {
          x: t("Wednesday"),
          y:
            tempNumberCanistersWednesday > 0 ? tempNumberCanistersWednesday : 0,
        },
        {
          x: t("Thursday"),
          y: tempNumberCanistersThursday > 0 ? tempNumberCanistersThursday : 0,
        },
        {
          x: t("Friday"),
          y: tempNumberCanistersFriday > 0 ? tempNumberCanistersFriday : 0,
        },
        {
          x: t("Saturday"),
          y: tempNumberCanistersSaturday > 0 ? tempNumberCanistersSaturday : 0,
        },
      ]);

      onCanisterProductSelectionLookup(tempCanisterProductSelectionLookup);

      // Note that canisterProducts is utilized only by DailyTotalBarchart for the Most Dispensed table
      onCanisterProducts(canisterProducts_temp);

      let tempPackageIdsList = "";

      tempPackageIdsList = tempPackageIdsList.substring(
        0,
        tempPackageIdsList.length - 1,
      );
    } else {
      onCanisterProducts([]);
      onCanisterProductsSelectedDay([]);
      onCanisterProductSelectionLookup({});
      setCanisterBargraphData([]);
    }
  }, [
    user,
    batchesSelectedDateRange,
    onCanisterProducts,
    onCanisterProductSelectionLookup,
    onCanisterProductsSelectedDay,
    t,
  ]);

  // SELECTED DATE RANGE
  useEffect(() => {
    /**
     * Sets dailyAverageCanisterSelectedDateRange
     *  - If selectedDayCanister is null, this is the average across the entire selected date range
     *  - If selectedDayCanister is set, this is the average for that specific day over the date range
     **/
    let tempDailyAverageCanisterSelectedDateRange;
    if (
      selectedDateRange?.startDate &&
      selectedDateRange?.endDate &&
      !applicableDates &&
      !selectedDayCanister &&
      selectedDayCanister !== 0
    ) {
      let totalCanistersSunday = 0;
      let totalCanistersMonday = 0;
      let totalCanistersTuesday = 0;
      let totalCanistersWednesday = 0;
      let totalCanistersThursday = 0;
      let totalCanistersFriday = 0;
      let totalCanistersSaturday = 0;
      let uniqueCanistersSunday = [];
      let uniqueCanistersMonday = [];
      let uniqueCanistersTuesday = [];
      let uniqueCanistersWednesday = [];
      let uniqueCanistersThursday = [];
      let uniqueCanistersFriday = [];
      let uniqueCanistersSaturday = [];

      canisterProducts.forEach((canisterProduct) => {
        switch (canisterProduct.dayOfWeek) {
          case 0:
            totalCanistersSunday += canisterProduct.canisterIds.length;
            uniqueCanistersSunday = combineTwoArrays(
              uniqueCanistersSunday,
              canisterProduct.canisterIds,
            );
            break;
          case 1:
            totalCanistersMonday += canisterProduct.canisterIds.length;
            uniqueCanistersMonday = combineTwoArrays(
              uniqueCanistersMonday,
              canisterProduct.canisterIds,
            );
            break;
          case 2:
            totalCanistersTuesday += canisterProduct.canisterIds.length;
            uniqueCanistersTuesday = combineTwoArrays(
              uniqueCanistersTuesday,
              canisterProduct.canisterIds,
            );
            break;
          case 3:
            totalCanistersWednesday += canisterProduct.canisterIds.length;
            uniqueCanistersWednesday = combineTwoArrays(
              uniqueCanistersWednesday,
              canisterProduct.canisterIds,
            );
            break;
          case 4:
            totalCanistersThursday += canisterProduct.canisterIds.length;
            uniqueCanistersThursday = combineTwoArrays(
              uniqueCanistersThursday,
              canisterProduct.canisterIds,
            );
            break;
          case 5:
            totalCanistersFriday += canisterProduct.canisterIds.length;
            uniqueCanistersFriday = combineTwoArrays(
              uniqueCanistersFriday,
              canisterProduct.canisterIds,
            );
            break;
          case 6:
            totalCanistersSaturday += canisterProduct.canisterIds.length;
            uniqueCanistersSaturday = combineTwoArrays(
              uniqueCanistersSaturday,
              canisterProduct.canisterIds,
            );
            break;
          default:
        }
      });

      let uniqueCanisters = [];

      canisterProducts.forEach((canisterProduct) => {
        uniqueCanisters = combineTwoArrays(
          uniqueCanisters,
          canisterProduct.canisterIds,
        );
      });

      tempDailyAverageCanisterSelectedDateRange = (
        uniqueCanisters.length /
        getNumberOfDaysInDateRange(
          selectedDateRange.startDate,
          selectedDateRange.endDate,
        )
      ).toFixed(1);

      setDailyAverageCanisterSelectedDateRange(
        tempDailyAverageCanisterSelectedDateRange,
      );
    } else if (selectedDayCanister || selectedDayCanister === 0) {
      let numCanistersForSelectedDay;
      switch (selectedDayCanister) {
        case 0:
          numCanistersForSelectedDay =
            totalDispensedCanisterSelectedDateRange.U.qty;
          break;
        case 1:
          numCanistersForSelectedDay =
            totalDispensedCanisterSelectedDateRange.M.qty;
          break;
        case 2:
          numCanistersForSelectedDay =
            totalDispensedCanisterSelectedDateRange.T.qty;
          break;
        case 3:
          numCanistersForSelectedDay =
            totalDispensedCanisterSelectedDateRange.W.qty;
          break;
        case 4:
          numCanistersForSelectedDay =
            totalDispensedCanisterSelectedDateRange.R.qty;
          break;
        case 5:
          numCanistersForSelectedDay =
            totalDispensedCanisterSelectedDateRange.F.qty;
          break;
        case 6:
          numCanistersForSelectedDay =
            totalDispensedCanisterSelectedDateRange.S.qty;
          break;
        default:
        // code block
      }

      if (applicableDates && applicableDates.length > 0) {
        tempDailyAverageCanisterSelectedDateRange =
          numCanistersForSelectedDay /
          getNumberOfWeekDayInDateRange(
            selectedDateRange.startDate,
            selectedDateRange.endDate,
            selectedDayCanister,
          );

        if (tempDailyAverageCanisterSelectedDateRange) {
          setDailyAverageCanisterSelectedDateRange(
            tempDailyAverageCanisterSelectedDateRange.toFixed(1),
          );
        } else {
          setDailyAverageCanisterSelectedDateRange((0.0).toFixed(1));
        }
      } else {
        setDailyAverageCanisterSelectedDateRange((0.0).toFixed(1));
      }
    } else {
      setDailyAverageCanisterSelectedDateRange((0.0).toFixed(1));
    }
  }, [
    selectedDateRange,
    totalDispensedCanisterSelectedDateRange,
    selectedDayCanister,
    applicableDates,
  ]);

  // Called when a selected day is clicked
  const handleCanisterProductsSelectedDay = (day) => {
    /**
     * Sets trayProductsSelectedDay
     * Note that this method also initializes the number of trays each product to 0
     * The tray count for each product is later calculated and updated by ...
     **/
    let canisterProducts_temp = [];
    let dayOfWeek;
    let batchesSelectedDay = batchesSelectedDateRange.filter(
      (batchSelectedDay) => {
        dayOfWeek = new Date(batchSelectedDay.createdAt).getDay();
        return dayOfWeek === day || (dayOfWeek === 0 && day === 0);
      },
    );

    let dispensingOrderIds = [];
    let tempApplicableDates = [];

    for (const batchSelectedDay of batchesSelectedDay) {
      if (!(batchSelectedDay.createdAt in tempApplicableDates)) {
        tempApplicableDates.push(batchSelectedDay.createdAt);
      }

      if (!dispensingOrderIds.includes(batchSelectedDay.dispensingOrderId)) {
        dispensingOrderIds.push(batchSelectedDay.dispensingOrderId);
      }
    }

    let canisterProductsSelectedTemp = [];
    canisterProducts_temp.sort(compareByQuantityDescending);

    canisterProducts.forEach((product) => {
      canisterProductsSelectedTemp.push({
        packageId: product.packageId,
        productName: product.productName,
        qty: product.qty,
        partialQty: product.partialQty,
        dayOfWeek: product.dayOfWeek,
      });
    });

    canisterProductsSelectedTemp = [
      ...canisterProductsSelectedTemp.filter(
        (product) => product.dayOfWeek === day,
      ),
    ];

    onCanisterProductsSelectedDay(canisterProductsSelectedTemp);
    tempApplicableDates.sort((a, b) => new Date(a) - new Date(b));
    onApplicableDates(tempApplicableDates);
  };

  /***************************************************************************************************************************/
  // This section will need to revamped to reflect change from pills to canisters

  // FOR BATCHES LAST WEEK
  useEffect(() => {
    /**
     * Sets:
     *  - totalDispensedPillsFromCanisterLastWeek
     **/
    let totalPillQuantity = 0;

    for (const batchLastWeek of batchesLastWeek) {
      for (const dispensingDetail of batchLastWeek.dispensingDetails) {
        if (!dispensingDetail.isTrayMed) {
          totalPillQuantity += dispensingDetail.quantity;
        }
      }
    }
    setTotalPillsDispensedFromCanisterLastWeek(totalPillQuantity);
  }, [batchesLastWeek]);

  // FOR BATCHES CURRENT WEEK
  useEffect(() => {
    /**
     * Sets:
     *  - totalDispensedPillsFromCanisterCurrentWeek
     **/
    let totalPillQuantity = 0;

    for (const batchCurrentWeek of batchesCurrentWeek) {
      for (const dispensingDetail of batchCurrentWeek.dispensingDetails) {
        if (!dispensingDetail.isTrayMed) {
          totalPillQuantity += dispensingDetail.quantity;
        }
      }
    }
    setTotalPillsDispensedFromCanisterCurrentWeek(totalPillQuantity);
  }, [batchesCurrentWeek]);

  // /***** Calculate percent increase from last week ****/
  let percentIncreaseFromLastWeek = NaN;

  if (totalPillsDispensedFromCanisterLastWeek > 0) {
    percentIncreaseFromLastWeek =
      ((totalPillsDispensedFromCanisterCurrentWeek -
        totalPillsDispensedFromCanisterLastWeek) /
        totalPillsDispensedFromCanisterLastWeek) *
      100;
  }

  let percentMessage = `${parseFloat(percentIncreaseFromLastWeek).toFixed(
    1,
  )}% ${t("increase from last week")}`;
  let IconIndicator = <ArrowUpCircle />;

  if (isNaN(percentIncreaseFromLastWeek)) {
    percentMessage = "No fills last week";
  } else if (percentIncreaseFromLastWeek < 0) {
    IconIndicator = <ArrowDownCircle />;
    percentMessage = `${(-parseFloat(percentIncreaseFromLastWeek)).toFixed(
      1,
    )}% ${t("decrease from last week")}`;
  }
  /***************************************************************************************************************************/

  const compareByQuantityDescending = (a, b) => {
    if (a.qty < b.qty) {
      return 1;
    } else if (a.qty > b.qty) {
      return -1;
    } else if (a.productName.toUpperCase() < b.productName.toUpperCase()) {
      return -1;
    }
    return 1;
  };

  const handleClassNameForBargraph = (name) => {
    setClassNameforBargraph(name);
  };

  const handleNullifyApplicableDates = () => {
    onApplicableDates(null);
  };

  // let startDateLocalized = getLocalizedDate(
  //   selectedDateRange.startDate,
  //   user?.defaultSite?.shippingAddress?.country,
  // );

  // let stopDateLocalized = getLocalizedDate(
  //   selectedDateRange.endDate,
  //   user?.defaultSite?.shippingAddress?.country,
  // );

  return (
    <>
      <div className={styles.MetricChartView__tableContainer}>
        <div className={styles.MetricChartView__bargraphHeadingContainer}>
          <div className={styles.MetricChartView__dailyAverageContainer}>
            <h3>{t("Daily Average")}</h3>
            <p className={styles.MetricChartView_dailyAverage}>
              {dailyAverageCanisterSelectedDateRange}
            </p>
          </div>
          {tableTitle}
          <div
            className={styles.MetricChartView__percentageFromLastWeekContainer}
          >
            {IconIndicator}
            <p>{percentMessage}</p>
          </div>
        </div>

        <DailyTotalBarChart
          user={user}
          stylesName="BarGraph"
          data={canisterBargraphData}
          chartWidth="700"
          chartHeight="300"
          onProductsSelectedDay={handleCanisterProductsSelectedDay}
          selectedDay={selectedDayCanister}
          onSelectedDay={onSelectedDayCanister}
          classNameForBargraph={classNameForBargraph}
          onClassNameForBargraph={handleClassNameForBargraph}
          productSelectionLookup={canisterProductSelectionLookup}
          onProductSelectionLookup={onCanisterProductSelectionLookup}
          onShowDropdowns={onShowDropdowns}
          onNullifyApplicableDates={handleNullifyApplicableDates}
        />
      </div>
    </>
  );
};

export default MetricsChartViewCanisters;
